/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { ReactNode } from 'react';

import CourseraPlusButtonListItem from 'bundles/composable-page-header/components/coursera-plus-button/CourseraPlusButtonListItem';
import MobileMenuConfigWrapper from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuConfigWrapper';
import AccountMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/AccountMenuItem';
import AccountSubMenu from 'bundles/composable-page-header/components/mobile-menu-items/AccountSubMenu';
import BrowseFreeCoursesMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/BrowseFreeCoursesMenuItem';
import WrappedExploreCustomConsumerSubmenu from 'bundles/composable-page-header/components/mobile-menu-items/ExploreCustomConsumerSubmenu';
import ExploreMenuItems from 'bundles/composable-page-header/components/mobile-menu-items/ExploreMenuItems';
import GetAppMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/GetAppMenuItem';
import LanguageTranslationSubMenu from 'bundles/composable-page-header/components/mobile-menu-items/LanguageTranslationSubMenu';
import MobileButton from 'bundles/composable-page-header/components/mobile-menu-items/MobileButton';
import ProgramList from 'bundles/composable-page-header/components/program-switcher/ProgramList';
import type {
  DegreeForProgramSwitcher as Degree,
  EnterpriseProgramForProgramSwitcher as EnterpriseProgram,
} from 'bundles/composable-page-header/components/program-switcher/types';
import { MOBILE_EXPLORE_IDENTIFIER, MOBILE_MAIN_IDENTIFIER } from 'bundles/composable-page-header/constants';
import getDefaultAccountDropdownLinks from 'bundles/composable-page-header/utils/getDefaultAccountDropdownLinks';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import { useIsFeedEnabled } from 'bundles/unified-home-common/hooks/useIsFeedEnabled';

import _t from 'i18n!nls/composable-page-header';

const styles = {
  myLearningWrapper: css`
    padding-bottom: var(--cds-spacing-100);
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    margin-bottom: var(--cds-spacing-100);
  `,
};

type Props = {
  enrolledDegreePrograms?: Array<Degree> | null;
  enrolledEnterprisePrograms?: Array<EnterpriseProgram> | null;
  isCourseraPlusLearner?: boolean | null;
  chosenDegree?: Degree | null;
  chosenEnterpriseProgram?: EnterpriseProgram | null;
  hasEnrolledPrograms?: boolean | null;
  naptimeManagedThirdPartyOrganizations?: Array<{ slug: string; name: string }> | null;
  hasCheatingIncidents?: boolean | null;
  canViewEducatorAdminLink?: boolean | null;
  courseraPlusSubscriptionCycle?: string | null;
  courseId?: string;
  cta?: ReactNode;
};

const DefaultLoggedInMobileMenuConfig = ({
  enrolledDegreePrograms,
  enrolledEnterprisePrograms,
  isCourseraPlusLearner,
  chosenDegree,
  chosenEnterpriseProgram,
  hasEnrolledPrograms,
  naptimeManagedThirdPartyOrganizations,
  hasCheatingIncidents,
  canViewEducatorAdminLink,
  courseraPlusSubscriptionCycle,
  courseId,
  cta,
}: Props) => {
  const myLearningButton = <MobileButton href="/my-learning" label={_t('My Learning')} name="myLearning" />;
  const feedButton = <MobileButton href="/feed" label={_t('Feed')} name="feed" />;
  const isFeedEnabled = useIsFeedEnabled();
  const isOrgScoped = GrowthDiscoveryEpicClient.preview('orgScopedHome');

  return (
    <MobileMenuConfigWrapper
      subPagesMap={{
        [MOBILE_MAIN_IDENTIFIER.ACCOUNT_MENU]: (
          <AccountSubMenu
            dropdownLinks={getDefaultAccountDropdownLinks({
              isDegreeOrEnterpriseLearner: !!hasEnrolledPrograms,
              managedThirdPartyOrganizations: naptimeManagedThirdPartyOrganizations,
              canViewEducatorAdminLink,
              hasCheatingIncidents,
            })}
            courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
          />
        ),
        [MOBILE_MAIN_IDENTIFIER.LANGUAGE_MENU]: <LanguageTranslationSubMenu currentCourseId={courseId} />,
        [MOBILE_MAIN_IDENTIFIER.EXPLORE_ROLES_MENU]: (
          <WrappedExploreCustomConsumerSubmenu identifier={MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_ROLES_SUBMENU} />
        ),
        [MOBILE_MAIN_IDENTIFIER.EXPLORE_CATEGORIES_MENU]: (
          <WrappedExploreCustomConsumerSubmenu
            identifier={MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_CATEGORIES_SUBMENU}
          />
        ),
        [MOBILE_MAIN_IDENTIFIER.EXPLORE_SKILLS_MENU]: (
          <WrappedExploreCustomConsumerSubmenu identifier={MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_SKILLS_SUBMENU} />
        ),
        [MOBILE_MAIN_IDENTIFIER.EARN_PROFESSIONAL_CERTIFICATE_MENU]: (
          <WrappedExploreCustomConsumerSubmenu
            identifier={MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_PROFESSIONAL_CERTIFICATE_SUBMENU}
          />
        ),
        [MOBILE_MAIN_IDENTIFIER.EARN_ONLINE_DEGREE_MENU]: (
          <WrappedExploreCustomConsumerSubmenu
            identifier={MOBILE_EXPLORE_IDENTIFIER.CONSUMER_EXPLORE_ONLINE_DEGREE_SUBMENU}
          />
        ),
      }}
    >
      {!isOrgScoped && <AccountMenuItem hasCourseraPlus={courseraPlusSubscriptionCycle != null} />}
      <div css={styles.myLearningWrapper}>
        {myLearningButton}
        {!isOrgScoped && isFeedEnabled && feedButton}
      </div>
      <ExploreMenuItems />
      <BrowseFreeCoursesMenuItem />
      {!isOrgScoped && <GetAppMenuItem />}
      {!hasEnrolledPrograms && !isCourseraPlusLearner && <CourseraPlusButtonListItem />}
      {!isOrgScoped && hasEnrolledPrograms && (
        <ProgramList
          currentProgram={chosenEnterpriseProgram}
          currentDegree={chosenDegree}
          programs={enrolledEnterprisePrograms || []}
          degrees={enrolledDegreePrograms || []}
          alwaysShowPersonalAccount
          trackingName="switcher_dropdown_program_option"
        />
      )}
      {cta}
    </MobileMenuConfigWrapper>
  );
};

export default DefaultLoggedInMobileMenuConfig;
